import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SearchBox } from "@/components/SearchBox";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SERVICES, ENTITY_KINDS, entityKindOf } from "@/lib/services";

export default function ServicePicker() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cfg = SERVICES[slug];
  const roleGated = !!cfg?.roleEntity;
  const [role, setRole] = useState("");
  // L'entità dipende dal ruolo per i servizi role-gated (es. Nido: Educatore->sala, Ausiliario->appalto).
  const effKindName = roleGated ? (role ? cfg.roleEntity[role] : null) : cfg?.entityKind;
  const kind = roleGated
    ? (effKindName ? ENTITY_KINDS[effKindName] : null)
    : entityKindOf(cfg);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const buildTarget = useCallback((entityId) => {
    let url = `/registro/${slug}/${entityId}`;
    if (roleGated) url += `?kind=${kind?.backendKind || effKindName}&role=${encodeURIComponent(role)}`;
    return url;
  }, [slug, roleGated, effKindName, role, kind]);

  const load = useCallback(async (q) => {
    if (!cfg || !kind) return;
    setLoading(true);
    try {
      const base = kind.listApi(cfg.key);
      const sep = base.includes("?") ? "&" : "?";
      const url = kind.searchable ? `${base}${sep}search=${encodeURIComponent(q || "")}` : base;
      const { data } = await api.get(url);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [cfg, kind]);

  useEffect(() => { if (kind) load(""); }, [load, kind]);

  useEffect(() => {
    if (!loading && kind && kind.autoSelectSingle && items.length === 1) {
      navigate(buildTarget(items[0].id), { replace: true });
    }
  }, [loading, items, kind, navigate, buildTarget]);

  useEffect(() => {
    if (!kind || !kind.searchable) return;
    const t = setTimeout(() => load(search), 250);
    return () => clearTimeout(t);
  }, [search, kind, load]);

  const filtered = !kind ? [] : kind.searchable
    ? items
    : items.filter((d) =>
        `${kind.title(d)} ${kind.subtitle(d)}`.toLowerCase().includes(search.toLowerCase())
      );

  if (!cfg) return null;
  const Icon = cfg.icon;
  const t = cfg.terms || {};
  const roleStep = roleGated && !role;
  const hint = roleStep ? "Seleziona il ruolo della prestazione" : (t.hint ?? kind?.hint);
  const searchPlaceholder = t.searchPlaceholder ?? kind?.searchPlaceholder;
  const emptyMessage = t.emptyMessage ?? kind?.emptyMessage;

  return (
    <div className="worker-theme min-h-screen bg-[#EDF0F4] pb-10">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 py-6">
        <button
          onClick={() => (roleGated && role ? setRole("") : navigate("/lavoratore"))}
          data-testid="picker-back"
          className="inline-flex items-center text-sm text-slate-500 hover:text-primary mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> {roleGated && role ? "Cambia ruolo" : "Home"}
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] flex items-center justify-center shadow-inner shrink-0">
            <Icon className="h-5 w-5 text-[#22364D]" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{cfg.label}</h1>
            <p className="text-sm text-slate-500">{hint}</p>
          </div>
        </div>

        {roleStep ? (
          <div className="space-y-2.5" data-testid="picker-role-step">
            {cfg.roles.map((r, i) => (
              <motion.button
                key={r} data-testid={`picker-role-${r}`}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: i * 0.05 }}
                onClick={() => { setSearch(""); setRole(r); }}
                className="w-full text-left rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.99] p-4 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800">{r}</p>
                  <p className="text-sm text-slate-500">
                    {cfg.roleEntity[r] === "appalto" ? "Prestazione sul Nido (senza sala)"
                      : cfg.roleEntity[r] === "school_user_ce" ? "Richiede la selezione di Utente/Gruppo"
                      : cfg.roleEntity[r] === "operative_site" ? "Richiede la selezione della sede"
                      : "Richiede la selezione della sala"}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
              </motion.button>
            ))}
          </div>
        ) : (
          <>
            {roleGated && (
              <div className="mb-4 flex items-center gap-2">
                <span className="text-xs text-slate-500">Ruolo:</span>
                <Badge data-testid="picker-selected-role" className="bg-primary/10 text-primary hover:bg-primary/10 border-0 text-xs font-semibold">{role}</Badge>
              </div>
            )}

            {kind?.searchable && (
              <div className="mb-4">
                <SearchBox value={search} onChange={setSearch} testid="picker-search-input" placeholder={searchPlaceholder} />
              </div>
            )}

            {loading ? (
              <p className="text-sm text-slate-500">Caricamento…</p>
            ) : filtered.length === 0 ? (
              <Card className="rounded-xl border-slate-200 shadow-sm">
                <CardContent className="p-6 text-center text-slate-500 text-sm" data-testid="picker-empty">
                  {emptyMessage}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2.5" data-testid="picker-results">
                {filtered.map((d, i) => (
                  <motion.button
                    key={d.id} data-testid={`picker-item-${d.id}`}
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: Math.min(i * 0.03, 0.3) }}
                    onClick={() => navigate(buildTarget(d.id))}
                    className="w-full text-left rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.99] p-4 flex items-center gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-800 truncate">{kind.title(d)}</p>
                        {kind.showAppaltoBadge && d.appalto_nome && (
                          <Badge data-testid={`picker-appalto-badge-${d.id}`}
                            className="bg-primary/10 text-primary hover:bg-primary/10 border-0 text-[10px] font-semibold">
                            {d.appalto_nome}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 truncate">{kind.subtitle(d)}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
                  </motion.button>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
